import * as THREE from 'three';
import type { RendererHandle } from '../../render/Renderer';
import {
  collectWebGPUCompatibilityReport,
  logWebGPUCompatibilityReport,
  mergeWebGPUCompatibilityReports,
} from '../../render/WebGPUCompatibility';
import { perf } from '../../debug/perf/PerfDiag';
import type { PerfGpuTimer } from '../../debug/perf/GpuTimer';
import type { System } from '../ServiceLocator';

type WebGPUCompatibilityReport = ReturnType<typeof collectWebGPUCompatibilityReport>;

export class RenderSystem implements System {
  readonly name = 'RenderSystem';

  webgpuCompatibilityReport: WebGPUCompatibilityReport | null = null;
  private stylePrewarmScheduled = false;
  private stylePrewarmRunning = false;
  private stylePrewarmDraining = false;
  private stylePrewarmQueued = false;
  private stylePrewarmMissLogged = false;

  constructor(
    readonly rendererHandle: RendererHandle,
    readonly scene: THREE.Scene,
    readonly gpuTimer: PerfGpuTimer | null,
  ) {}

  get renderer() { return this.rendererHandle.renderer; }
  get webglRenderer(): THREE.WebGLRenderer | null { return this.rendererHandle.webglRenderer; }

  // Some WebGPU custom materials don't map cleanly to Three's WebGPU
  // renderer. Collect a report at warmup so degraded materials surface
  // in the diag overlay instead of silently rendering wrong.
  registerWebGPUCompatibility = (): void => {
    if (!this.rendererHandle.isWebGPU) return;
    const report = collectWebGPUCompatibilityReport(this.scene);
    if (report.unsupportedMaterials.length > 0) logWebGPUCompatibilityReport(report);
    this.webgpuCompatibilityReport = mergeWebGPUCompatibilityReports(this.webgpuCompatibilityReport, report);
  };

  prewarmAfterStyleChange(
    camera: THREE.Camera,
    options: { enabled?: boolean; reason?: string } = {},
  ): void {
    if (options.enabled === false || !this.rendererHandle.isWebGPU) return;
    if (this.stylePrewarmRunning || this.stylePrewarmScheduled || this.stylePrewarmDraining) {
      this.stylePrewarmQueued = true;
      return;
    }
    this.stylePrewarmScheduled = true;
    nextAnimationFrame(() => {
      this.stylePrewarmScheduled = false;
      void this.runStylePrewarm(camera, options.reason ?? 'style-change');
    });
  }

  /** Fields this system contributes to the window.__twin debug surface. */
  debugEntries(): Record<string, unknown> {
    return {
      scene: this.scene,
      renderer: this.renderer,
    };
  }

  /** Snapshot of renderer state for diag overlay / e2e tests. Called
   *  fresh on every diag read so values are live. */
  rendererDiagnostics(): Record<string, unknown> {
    const handle = this.rendererHandle;
    const r = this.renderer;
    return {
      mode: handle.mode,
      requestedMode: handle.requestedMode,
      fallbackReason: handle.fallbackReason,
      pixelRatio: r.getPixelRatio(),
      shadowMapEnabled: r.shadowMap.enabled,
      shadowMapAutoUpdate: this.webglRenderer?.shadowMap.autoUpdate ?? null,
      shadowMapNeedsUpdate: this.webglRenderer?.shadowMap.needsUpdate ?? null,
      postProcessing: handle.capabilities.postProcessing,
      stylePrewarm: {
        scheduled: this.stylePrewarmScheduled,
        running: this.stylePrewarmRunning,
        draining: this.stylePrewarmDraining,
        queued: this.stylePrewarmQueued,
      },
    };
  }

  private async runStylePrewarm(camera: THREE.Camera, reason: string): Promise<void> {
    if (this.stylePrewarmRunning) {
      this.stylePrewarmQueued = true;
      return;
    }
    this.stylePrewarmRunning = true;
    const startedAt = performance.now();
    perf.event('prewarm.start', { reason });

    const compilePromise = this.renderer.compileAsync(this.scene, camera);
    let ended = false;
    const emitEnd = (result: 'compiled' | 'timeout' | 'failed'): void => {
      if (ended) return;
      ended = true;
      perf.event('prewarm.end', { reason, result, ms: performance.now() - startedAt });
    };
    try {
      const result = await Promise.race([
        compilePromise.then(() => 'compiled' as const),
        twoAnimationFrames().then(() => 'timeout' as const),
      ]);
      if (result === 'timeout' && !this.stylePrewarmMissLogged) {
        this.stylePrewarmMissLogged = true;
        console.info('[render] style prewarm exceeded the two-frame budget; compile continues in the background');
      }
      if (result === 'timeout') {
        this.stylePrewarmRunning = false;
        this.stylePrewarmDraining = true;
        emitEnd('timeout');
        void compilePromise
          .then(() => this.registerWebGPUCompatibility())
          .catch((err) => {
            console.warn('[render] style prewarm failed after timeout; falling back to lazy compile', err);
          })
          .finally(() => this.finishStylePrewarm(camera, reason));
        return;
      }
      this.registerWebGPUCompatibility();
      emitEnd('compiled');
    } catch (err) {
      console.warn('[render] style prewarm failed; falling back to lazy compile', err);
      emitEnd('failed');
    } finally {
      if (!this.stylePrewarmDraining) this.finishStylePrewarm(camera, reason);
    }
  }

  private finishStylePrewarm(camera: THREE.Camera, reason: string): void {
    this.stylePrewarmRunning = false;
    this.stylePrewarmDraining = false;
    if (this.stylePrewarmQueued) {
      this.stylePrewarmQueued = false;
      this.prewarmAfterStyleChange(camera, { reason });
    }
  }
}

function nextAnimationFrame(fn: () => void): void {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => fn());
  } else {
    setTimeout(fn, 0);
  }
}

function twoAnimationFrames(): Promise<void> {
  return new Promise((resolve) => {
    nextAnimationFrame(() => nextAnimationFrame(resolve));
  });
}
